import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollText } from "lucide-react";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

// Phase 18 — Audit log uses SERVER-side filtering for the heavy queries
// (action_prefix, actor_email, target_id, date_from/date_to) because the
// collection can grow unbounded. The same FilterPanel renders the controls,
// but each schema change refetches from /api/admin/audit-logs.
export default function AdminAudit() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverFilters, setServerFilters] = useState({
    action_prefix: "", actor_email: "", target_id: "", target_type: "",
    date_from: "", date_to: "",
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      Object.entries(serverFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [
    serverFilters.actor_email, serverFilters.action_prefix, serverFilters.target_type,
    serverFilters.target_id, serverFilters.date_from, serverFilters.date_to,
  ]);

  // Schema is rendered by FilterPanel BUT values live in serverFilters.
  // We shim useFilters around an empty client list, just to drive the panel.
  const schemas = useMemo(() => [
    { key: "actor_email", type: "text", label: "Actor email", path: "actor_email", placeholder: "e.g. admin@" },
    { key: "action_prefix", type: "text", label: "Action prefix", path: "action", placeholder: "e.g. credentials." },
    { key: "target_type", type: "select", label: "Record type", path: "target_type", options: [
      { value: "credentials", label: "Credentials" },
      { value: "licenses", label: "Licenses" },
      { value: "schools", label: "Schools" },
      { value: "school_applications", label: "School applications" },
      { value: "applications", label: "Applications" },
      { value: "user", label: "Users" },
    ] },
    { key: "target_id", type: "text", label: "Record ID", path: "target_id", placeholder: "exact id" },
    { key: "date_from", type: "dateFrom", label: "From", path: "created_at" },
    { key: "date_to", type: "dateTo", label: "To", path: "created_at" },
    { key: "ip", type: "text", label: "IP address", path: "ip" },
  ], []);

  // Local client filter just for the IP field (server doesn't expose IP filter currently)
  const ipFilter = serverFilters.ip || "";
  const displayed = ipFilter
    ? logs.filter((l) => (l.ip || "").includes(ipFilter))
    : logs;

  const selection = useSelection(displayed);

  const fakeFilters = {
    values: serverFilters,
    set: (k, v) => setServerFilters((s) => ({ ...s, [k]: v })),
    clear: () => setServerFilters({
      action_prefix: "", actor_email: "", target_id: "", target_type: "",
      date_from: "", date_to: "", ip: "",
    }),
    count: displayed.length,
    activeCount: Object.values(serverFilters).filter((v) => !!v).length,
    sort: null,
    setSort: () => {},
  };

  return (
    <div className="animate-fade-up" data-testid="audit-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-primary">Audit log</h1>
          <p className="text-sm text-slate-500 mt-1">Immutable record of credentialing actions. Used for compliance review.</p>
        </div>
        <button onClick={fetchLogs} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50" data-testid="audit-apply">
          Apply filters
        </button>
      </div>

      <FilterPanel
        schemas={schemas}
        values={fakeFilters.values}
        set={fakeFilters.set}
        clear={fakeFilters.clear}
        count={fakeFilters.count}
        total={logs.length}
        activeCount={fakeFilters.activeCount}
        testid="audit-filters"
      />

      {isAdmin && (
        <>
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="audit-bulk-warning">
            <b>Audit logs are immutable compliance records.</b> Bulk deletion is available for
            System Administrators to remove noise from test environments, but each action is
            itself recorded in a new audit entry. Do not purge logs from production without a
            documented retention-policy exception.
          </div>
          <BulkActionsBar collection="audit_logs" selection={selection} onDone={fetchLogs} />
        </>
      )}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="p-3 w-10">
                    <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="audit-select-all" />
                  </th>
                )}
                <th className="p-3">When</th>
                <th className="p-3">Actor</th>
                <th className="p-3">Action</th>
                <th className="p-3">Target</th>
                <th className="p-3">IP</th>
                <th className="p-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={isAdmin ? 7 : 6} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && displayed.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center">
                  <ScrollText className="h-9 w-9 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No log entries</div>
                </td></tr>
              )}
              {displayed.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                  {isAdmin && (
                    <td className="p-3">
                      <Checkbox checked={selection.isSelected(l.id)} onCheckedChange={() => selection.toggle(l.id)} data-testid={`audit-select-${l.id}`} />
                    </td>
                  )}
                  <td className="p-3 text-xs text-slate-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <div className="text-xs font-medium text-primary">{l.actor_email || "system"}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{l.actor_role || "—"}</div>
                  </td>
                  <td className="p-3"><Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono-data text-[10px]">{l.action}</Badge></td>
                  <td className="p-3 text-xs">
                    <div className="text-slate-700">{l.target_type || "—"}</div>
                    <div className="font-mono-data text-[10px] text-slate-500">{l.target_id ? l.target_id.slice(0, 12) + "…" : ""}</div>
                  </td>
                  <td className="p-3 text-[11px] font-mono-data text-slate-500">{l.ip || ""}</td>
                  <td className="p-3 text-[11px] text-slate-600 max-w-xs truncate">{l.details ? JSON.stringify(l.details) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
